import { Divider, Typography } from '@mui/material';
import { concat, get, isArray } from 'lodash';
import React, { FC, useMemo } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MarkdownRenderer from 'src/components/ui-component/markdown-renderer/MarkdownRenderer';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	ANGABEN_ZUM_VORSTEUERABZUG_LOEWE,
	ANGABEN_ZUM_VORSTEUERABZUG_ORGANISATION,
	ANTRAG_TYP,
	ART_DES_ANTRAGS,
	AUF_GESAMT,
	AUSGABENPLAN,
	FINANZIERUNGSPLAN,
	FORM_FIELDS_TYPES,
	KOSTEN_ARTEN,
	KOSTENTRAEGER,
} from 'src/lib/constants/constant';
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenpositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue } from 'src/lib/validation/ValidationTypes';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import {
	checkIfPruefAssitenzNeedsToValidate,
	getVerbundPartnerName,
} from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { checkReadOnlyInUeberarbeitung } from './helper';

const P = AntragSchema.attributes;
const KP_P = KostenpositionSchema.attributes;

export function PruefAssistenzAUF() {
	return (
		<>
			<PruefAssistenz
				statusPath={P.Ausgabenplan_Pruefung_Pruefstatus.path}
				hinweisPath={P.Ausgabenplan_Pruefung_Hinweis_Fuer_Den_Antragsteller.path}
				ueberarbeitetAmPath={P.Ausgabenplan_Pruefung_Ueberarbeitet_Am.path}
				erledigtPath={P.Ausgabenplan_Pruefung_Erledigt.path}
				formStruc={formStrucAusgabenplan()}
				label={AUSGABENPLAN}
				sx={{ mt: 3 }}
			/>
			<PruefAssistenz
				statusPath={P.Finanzierungsplan_Pruefung_Pruefstatus.path}
				hinweisPath={P.Finanzierungsplan_Pruefung_Hinweis_Fuer_Den_Antragsteller.path}
				ueberarbeitetAmPath={P.Finanzierungsplan_Pruefung_Ueberarbeitet_Am.path}
				erledigtPath={P.Finanzierungsplan_Pruefung_Erledigt.path}
				formStruc={formStrucFinanzierungsplan()}
				label={FINANZIERUNGSPLAN}
				sx={{ mt: 3 }}
			/>
			<PruefAssistenz
				statusPath={P.AUF_Gesamt_Pruefung_Pruefstatus.path}
				hinweisPath={P.AUF_Gesamt_Pruefung_Hinweis_Fuer_Den_Antragsteller.path}
				ueberarbeitetAmPath={P.AUF_Gesamt_Pruefung_Ueberarbeitet_Am.path}
				erledigtPath={P.AUF_Gesamt_Pruefung_Erledigt.path}
				formStruc={formStrucAUF()}
				label={AUF_GESAMT}
				sx={{ mt: 3 }}
			/>
		</>
	);
}

const formStrucAusgabenplan = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Ausgabenplan_Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];

	return struc;
};

const formStrucFinanzierungsplan = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Finanzierungsplan_Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];

	return struc;
};

const formStrucAUF = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.AUF_Gesamt_Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];

	return struc;
};

export const getAuFPruefAssistenzFormStruc = (application: any, result: FormStrucItem[]) => {
	if (checkIfPruefAssitenzNeedsToValidate(application, P.Ausgabenplan_Pruefung_Pruefstatus.path, P.Ausgabenplan_Pruefung_Erledigt.path)) {
		result = concat(formStrucAusgabenplan(), result);
	}
	if (checkIfPruefAssitenzNeedsToValidate(application, P.Finanzierungsplan_Pruefung_Pruefstatus.path, P.Finanzierungsplan_Pruefung_Erledigt.path)) {
		result = concat(formStrucFinanzierungsplan(), result);
	}
	if (checkIfPruefAssitenzNeedsToValidate(application, P.AUF_Gesamt_Pruefung_Pruefstatus.path, P.AUF_Gesamt_Pruefung_Erledigt.path)) {
		result = concat(formStrucAUF(), result);
	}
	return result;
};

export const getKostenHinweisStruc = (
	application: any,
	kostenposition: string,
	kostenart: string,
	antragTyp: string,
	hasComment?: boolean,
	showVorsteuer?: boolean,
) => {
	let fpHinweise;
	if (antragTyp === ANTRAG_TYP.Skizze) {
		fpHinweise = get(application, P.Foerderprogramm.schema.attributes.Hinweise_Zur_Erfassung_Der_Kosten_Skizze.path, []);
	} else {
		const artDesAntrags = get(application, P.Allgemein_Art_Des_Antrags.path, '');
		if (artDesAntrags === ART_DES_ANTRAGS.Formantrag) {
			fpHinweise = get(application, P.Foerderprogramm.schema.attributes.Hinweise_Zur_Erfassung_Der_Kosten_Im_Formantrag.path, []);
		} else {
			fpHinweise = get(application, P.Foerderprogramm.schema.attributes.Hinweise_Zur_Erfassung_Der_Kosten_Antrag.path, []);
		}
	}
	const hinweiseArray = isArray(fpHinweise) ? fpHinweise : [];

	if (hinweiseArray.length === 0) return [];

	if (kostenart === KOSTEN_ARTEN.Sachkosten) {
		return hinweiseArray
			.filter((eachHinweis: any) => get(eachHinweis, 'Kostenposition', '') === kostenposition && get(eachHinweis, 'Kostenart', '') === kostenart)
			.map((eachHinweis: any, index: number) => ({
				label: get(eachHinweis, 'Titel', '') || 'Hinweis',
				content: (
					<>
						<HinweisContent hinweisObject={eachHinweis} hasComment={hasComment} showVorsteuer={Boolean(showVorsteuer) && index === 0} />
					</>
				),
			}));
	}

	return hinweiseArray
		.filter((eachHinweis: any) => get(eachHinweis, 'Kostenposition', '') === kostenposition && get(eachHinweis, 'Kostenart', '') === kostenart)
		.map((eachHinweis: any) => {
			const untertitel = get(eachHinweis, 'Untertitel', '');

			return {
				label: get(eachHinweis, 'Titel', '') || 'Hinweis',
				content: (
					<>
						{untertitel && (
							<Typography variant='h6' sx={{ mb: 3, mt: 1 }} fontWeight={'bold'}>
								{untertitel}
							</Typography>
						)}
						<MarkdownRenderer markdown={get(eachHinweis, 'Inhalt', '')} />
					</>
				),
			};
		});
};

const HinweisContent: FC<{ hinweisObject: any; hasComment?: boolean; showVorsteuer?: boolean }> = ({ hinweisObject, hasComment, showVorsteuer }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);
	const inhalt = get(hinweisObject, 'Inhalt', '');
	const untertitel = get(hinweisObject, 'Untertitel', '');
	const showHinweisVorsteuerabzugWiderspricht = useMemo(() => {
		const orgaVorsteuerabzug = get(
			application,
			P.Antragstellende_Organisation.schema.attributes.Unternehmensprofil_Angaben_Zum_Vorsteuerabzug.path,
		);
		const sachkostenVorsteuerabzug = get(
			application,
			P.Projektbeschreibung_Loewe.schema.attributes.Projektuebersicht.schema.attributes.Vorsteuerberechtigt.path,
		);

		// Wenn einer der Werte leer/nicht gesetzt ist, return false
		if (!orgaVorsteuerabzug || !sachkostenVorsteuerabzug) {
			return false;
		}

		//TODO check with LOEWE
		if (
			(orgaVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_ORGANISATION.Vorsteuerabzugsberechtigt &&
				sachkostenVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_LOEWE.Vorsteuerabzugsberechtigt) ||
			(orgaVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_ORGANISATION.keineVorsteuerabzugsberechtigung &&
				sachkostenVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_LOEWE.keineVorsteuerabzugsberechtigung)
		) {
			return false;
		}

		return true;
	}, [application]);
	return (
		<>
			{untertitel && (
				<Typography variant='h6' sx={{ mb: 3, mt: 1 }} fontWeight={'bold'}>
					{untertitel}
				</Typography>
			)}
			<MarkdownRenderer markdown={inhalt} />
			{showVorsteuer && (
				<>
					<Divider sx={{ borderBottomWidth: 1, borderColor: 'black', mt: 1, mb: 2 }} />
					<Typography variant='h6' sx={{ mb: 3, mt: 1 }} fontWeight={'bold'}>
						Erklärung zum Vorsteuerabzug
					</Typography>
					<Typography>
						<strong>Bitte beachten Sie:</strong> Bei Vorsteuerabzugsberechtigung sind die Sachkosten als <u>Netto-Beträge</u> anzusetzen. Um die
						von Ihnen angegebenen Kosten korrekt zu bewerten, geben Sie bitte hier an, was auf die von Ihnen gemachten Angaben und Ihren Status
						zutrifft:
					</Typography>

					{showHinweisVorsteuerabzugWiderspricht && (
						<MUIAlert
							infoType={'warning'}
							message={
								'Die hier abgegebene Erklärung zum Vorsteuerabzug widerspricht den von Ihnen gemachten Angaben im Organisationsprofil. Bitte überprüfen Sie Ihre Angaben und korrigieren Sie diese an der betreffenden Stelle.'
							}
						/>
					)}

					<CreateForm
						formStruc={vorsteuerStruc()}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={updatedReadOnly}
					/>
					<Typography sx={{ mt: 2 }} fontWeight={'bold'}>
						Sollten sich während der Projektlaufzeit Änderungen an der Vorsteuerabzugsberechtigung ergeben, so sind diese der
						Bewilligungsbehörde unverzüglich mitzuteilen.
					</Typography>
					<Divider sx={{ borderBottomWidth: 1, borderColor: 'black', mt: 2, mb: 3 }} />
				</>
			)}
		</>
	);
};

const vorsteuerStruc = () => {
	return [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Projektuebersicht.schema.attributes.Vorsteuerberechtigt,
					type: FORM_FIELDS_TYPES.radio,
					direction: 'column',
					label: '',
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
};

export const getUnternehmenDisplayName = (application: any, ele: any) => {
	const verbundPartner = get(application, P.Verbundpartner.path, []);

	const fallbackName = get(ele, KP_P.Unternehmen.schema.attributes.Geschaeftsbezogene_Daten_Name_Der_Organisation.path, '');
	const isVerbundPartner = get(ele, KP_P.Allgemein_Kostentraeger.path) === KOSTENTRAEGER.Verbundpartner;

	if (!isVerbundPartner) {
		return fallbackName;
	}

	const unternehmenId = get(ele, `${KP_P.Unternehmen.path}.id`);
	const matchedVerbundpartnerIndex = verbundPartner.findIndex(
		(vPartnerEle: any) => String(get(vPartnerEle, 'Verbundpartner.id', '')) === String(unternehmenId),
	);

	if (matchedVerbundpartnerIndex < 0) {
		return fallbackName;
	}

	const matchedVerbundpartner = verbundPartner[matchedVerbundpartnerIndex];
	const partnerName = getVerbundPartnerName(matchedVerbundpartner, matchedVerbundpartnerIndex);

	return partnerName || fallbackName;
};
